import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Copy, MessageCircle, Phone, Loader2, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getAtRisk, updateAtRiskCase } from "@/lib/api";
import { numberLocale } from "@/lib/money";

// At-risk members (backend routes/at_risk.py): who has stopped coming, is coming less, hasn't started,
// or isn't renewing, ranked by what their membership is worth. Each one is a case: the member manager
// sends the suggested WhatsApp themselves and logs what happened. Cases close by themselves when the
// member comes back in or renews.

const err = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;
const fmt = (n) => new Intl.NumberFormat(numberLocale()).format(Math.round(n || 0));
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "");
const TINT = {
  stopped: "bg-rose-500/15 text-rose-200 border-rose-500/40",
  no_start: "bg-rose-500/15 text-rose-200 border-rose-500/40",
  slowing: "bg-amber-500/15 text-amber-200 border-amber-500/40",
  expiring: "bg-amber-500/15 text-amber-200 border-amber-500/40",
  lapsed: "bg-violet-500/15 text-violet-200 border-violet-500/40",
};
const waLink = (phone, text) => {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return null;
  const full = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${full}?text=${encodeURIComponent(text)}`;
};

function Case({ m, outcomes, onSaved }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (outcome) => {
    setBusy(true);
    try {
      const r = await updateAtRiskCase(m.case_id, { outcome, note });
      toast.success(r.closed ? `${m.name}: closed as ${outcomes[outcome].toLowerCase()}` : "Saved");
      setNote(""); setOpen(false); onSaved();
    } catch (e) { toast.error(err(e, "Couldn't save")); }
    finally { setBusy(false); }
  };
  const wa = waLink(m.phone, m.message);
  return (
    <li className="card-surface p-4" data-testid={`at-risk-${m.case_id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-zinc-100">{m.name}</span>
            <span className={`text-[11px] px-2 py-0.5 rounded-full border ${TINT[m.reason]}`}>{m.reason_label}</span>
            {m.outcome === "contacted" && <span className="text-[11px] px-2 py-0.5 rounded-full border border-sky-500/40 text-sky-200">Contacted</span>}
          </div>
          <div className="text-xs text-zinc-400 mt-1 space-x-2">
            <span>{m.programme || "Membership"}{m.end_date ? `, ${m.days_left >= 0 ? `ends ${day(m.end_date)} (${m.days_left} days)` : `ended ${day(m.end_date)}`}` : ""}</span>
            <span>· worth {window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmt(m.worth)}</span>
            <span>· {m.last_visit ? `last in ${day(m.last_visit)}` : "no check-ins"}{m.visits_30d ? `, ${m.visits_30d} in 30 days` : ""}</span>
            {m.points >= 50 && <span>· {fmt(m.points)} points</span>}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">
            {m.member_manager ? `Member manager: ${m.member_manager}` : "No member manager"}{m.coach ? ` · Coach: ${m.coach}` : ""}
          </div>
          {m.notes.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-xs text-zinc-400">
              {m.notes.slice(-3).map((n, i) => <li key={i}>{day(n.at)} · {n.by}: {n.outcome ? `${outcomes[n.outcome]}. ` : ""}{n.text}</li>)}
            </ul>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {wa && (
            <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-sm text-white"
              data-testid="at-risk-whatsapp"><MessageCircle className="h-4 w-4" />WhatsApp</a>
          )}
          {m.phone && <a href={`tel:${m.phone}`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-[#2a2a2a] text-sm text-zinc-200"><Phone className="h-4 w-4" />Call</a>}
          <button type="button" onClick={() => setOpen((v) => !v)} className="h-9 px-3 rounded-full border border-[#2a2a2a] text-sm text-zinc-200"
            data-testid="at-risk-log">Log what happened</button>
        </div>
      </div>
      <div className="mt-3 rounded-lg border border-[#1f1f1f] bg-[#0a0a0a] p-3 text-sm text-zinc-300 flex items-start gap-2">
        <span className="flex-1">{m.message}</span>
        <button type="button" title="Copy message" className="text-zinc-500 hover:text-zinc-200"
          onClick={() => { navigator.clipboard?.writeText(m.message); toast.success("Message copied"); }}><Copy className="h-4 w-4" /></button>
      </div>
      {open && (
        <div className="mt-3 space-y-2">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="What did they say? (needed if they're leaving)"
            className="w-full bg-[#0a0a0a] border border-[#262626] rounded-md px-3 py-2 text-sm text-zinc-100" data-testid="at-risk-note" />
          <div className="flex flex-wrap gap-2">
            {Object.entries(outcomes).map(([k, label]) => (
              <button key={k} type="button" disabled={busy} onClick={() => save(k)} data-testid={`at-risk-outcome-${k}`}
                className={`h-8 px-3 rounded-full text-xs border ${k === "leaving" ? "border-rose-500/40 text-rose-200" : "border-[#2a2a2a] text-zinc-200"}`}>
                {label}
              </button>
            ))}
            {note.trim() && <button type="button" disabled={busy} onClick={() => save(null)} className="h-8 px-3 rounded-full text-xs bg-zinc-100 text-zinc-900">Save note only</button>}
          </div>
        </div>
      )}
    </li>
  );
}

export default function AtRisk() {
  const { activeFacility } = useAuth();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = useCallback(() => {
    getAtRisk().then(setData).catch((e) => toast.error(err(e, "Couldn't load the at-risk list")));
  }, []);
  useEffect(() => { setData(null); load(); }, [load, activeFacility?.id]);

  const members = useMemo(() => (data?.members || []).filter((m) => filter === "all" || m.reason === filter), [data, filter]);
  const counts = useMemo(() => {
    const c = {};
    (data?.members || []).forEach((m) => { c[m.reason] = (c[m.reason] || 0) + 1; });
    return c;
  }, [data]);
  const worth = (data?.members || []).reduce((s, m) => s + (m.worth || 0), 0);

  return (
    <div className="space-y-6" data-testid="at-risk-page">
      <div>
        <div className="eyebrow">Retention · {activeFacility?.name || "this gym"}</div>
        <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">At-risk members</h2>
        <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
          Members who have stopped coming, are coming less, haven&apos;t started, or aren&apos;t renewing. Most valuable first.
          Send the WhatsApp from your phone, then log what happened. A case closes by itself when they check in again or renew.
        </p>
      </div>

      {!data && <div className="text-zinc-500 text-sm inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading</div>}

      {data && !data.attendance_ready && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 text-sm text-amber-100 flex gap-2" data-testid="at-risk-no-history">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-300" />
          <span>
            {data.checkins_since
              ? `Check-ins here go back to ${day(data.checkins_since)}. Members who stop coming show up once there are 4 weeks of them (from ${day(data.attendance_from)}).`
              : "No check-ins at this gym yet. Members who stop coming show up once there are 4 weeks of check-ins."}
            {" "}Until then this list is renewals only.
          </span>
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="card-surface p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">At risk now</div>
              <div className="font-display text-2xl font-extrabold mt-1 text-rose-300">{data.members.length}</div>
            </div>
            <div className="card-surface p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Their memberships are worth</div>
              <div className="font-display text-2xl font-extrabold mt-1 text-amber-300">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmt(worth)}</div>
            </div>
            <div className="card-surface p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-400" />Back or renewing this month</div>
              <div className="font-display text-2xl font-extrabold mt-1 text-emerald-300">{(data.this_month.back || 0) + (data.this_month.renewing || 0)}</div>
            </div>
            <div className="card-surface p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Frozen / leaving this month</div>
              <div className="font-display text-2xl font-extrabold mt-1 text-zinc-300">{data.this_month.freeze || 0} / {data.this_month.leaving || 0}</div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {[["all", `All ${data.members.length}`], ...Object.entries(data.reasons).map(([k, v]) => [k, `${v} ${counts[k] || 0}`])].map(([k, label]) => (
              <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`at-risk-filter-${k}`}
                className={`h-8 px-3 rounded-full text-xs ${filter === k ? "bg-zinc-100 text-zinc-900" : "border border-[#2a2a2a] text-zinc-400"}`}>{label}</button>
            ))}
          </div>

          {members.length === 0
            ? <div className="card-surface p-6 text-sm text-zinc-400">Nobody here right now.</div>
            : <ul className="space-y-3">{members.map((m) => <Case key={m.case_id} m={m} outcomes={data.outcomes} onSaved={load} />)}</ul>}
        </>
      )}
    </div>
  );
}
